import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useWallet } from '../../contexts/WalletContext';

/** Solid white network wordmark — reference-style bottom corner */
const VisaWordmark = () => (
  <span
    aria-hidden
    style={{
      color: '#FFFFFF',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '18px',
      fontWeight: 900,
      fontStyle: 'italic',
      letterSpacing: '0.02em',
      lineHeight: 1,
      flexShrink: 0,
    }}
  >
    VISA
  </span>
);

/**
 * Navbar-style “S” tile (gold or white-fill). `outlineWhite` = white strokes + glyph on transparent
 * — same silhouette as Navbar, monochrome for dark/Gold cards.
 */
const SimoAppMark = ({ size = 36, lite = false, outlineWhite = false }) => {
  const r = Math.round((12 * size) / 36);
  const strokePx = Math.max(2, Math.round((size * 2.2) / 36));
  if (outlineWhite) {
    return (
      <div
        aria-hidden
        style={{
          width: `${size}px`,
          height: `${size}px`,
          boxSizing: 'border-box',
          borderRadius: `${r}px`,
          background: 'transparent',
          border: `${strokePx}px solid rgba(255,255,255,0.95)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: `${Math.round(size * 0.52)}px`,
          fontWeight: '800',
          color: '#FFFFFF',
          fontFamily: 'Inter, system-ui, sans-serif',
          flexShrink: 0,
          lineHeight: 1,
        }}
      >
        S
      </div>
    );
  }
  return (
    <div
      aria-hidden
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `${r}px`,
        background: lite
          ? 'linear-gradient(145deg, #FFFFFF 0%, #E8EAEF 100%)'
          : 'linear-gradient(135deg, #D4AF37 0%, #B8962E 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${Math.round(size * 0.5)}px`,
        fontWeight: '800',
        color: '#0F0F0F',
        fontFamily: 'Inter, system-ui, sans-serif',
        boxShadow: lite ? '0 2px 12px rgba(0,0,0,0.22)' : '0 3px 14px rgba(212,175,55,0.38)',
        border: lite ? '1px solid rgba(255,255,255,0.42)' : 'none',
        flexShrink: 0,
      }}
    >
      S
    </div>
  );
};

/** EMV graphic from `public/assets/images/wallet/chip.svg` — physical cards only */
const PhysicalCardEmvChip = () => (
  <img
    src={`${process.env.PUBLIC_URL ?? ''}/assets/images/wallet/chip.svg`}
    alt=""
    aria-hidden
    draggable={false}
    style={{
      width: '52px',
      height: 'auto',
      maxHeight: '34px',
      display: 'block',
      flexShrink: 0,
      objectFit: 'contain',
    }}
  />
);

/**
 * Minimal card face — corners only, open center.
 * Virtual: matte black. Physical: warm golden card body.
 */
const SimoCardFace = ({ variant, rtl, panLast4, isPhysical }) => {
  const isIssued = variant === 'issued';
  const matteBase =
    'radial-gradient(ellipse 92% 88% at 50% 48%, #2a2a2a 0%, #1f1f1f 38%, #1a1a1a 62%, #121212 100%)';
  const goldBase =
    'radial-gradient(ellipse 100% 85% at 65% -5%, rgba(255,226,172,0.35) 0%, transparent 48%), radial-gradient(ellipse 75% 70% at 12% 100%, rgba(120,72,28,0.45) 0%, transparent 52%), linear-gradient(152deg, #3d3222 0%, #241c14 42%, #1a1510 100%)';

  return (
    <div
      aria-hidden={!isIssued}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 'none',
        aspectRatio: '85.6 / 53.98',
        height: 'auto',
        alignSelf: 'stretch',
        borderRadius: '22px',
        overflow: 'hidden',
        border: isPhysical ? '1px solid rgba(212,175,55,0.55)' : '1px solid rgba(255,255,255,0.035)',
        boxShadow: isPhysical
          ? '0 18px 44px rgba(0,0,0,0.55), 0 0 32px rgba(212,175,55,0.12)'
          : '0 18px 42px rgba(0,0,0,0.65)',
        background: isPhysical ? goldBase : matteBase,
      }}
    >
      {!isPhysical ? (
        <img
          src={`${process.env.PUBLIC_URL ?? ''}/assets/images/wallet/card-black-bg.svg`}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: '50% 50%',
            opacity: 0.62,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      ) : (
        <img
          src={`${process.env.PUBLIC_URL ?? ''}/assets/images/wallet/card-golden-bg.svg`}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: '50% 50%',
            opacity: 0.64,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          opacity: isPhysical ? 0.48 : 0.42,
          background: isPhysical
            ? 'radial-gradient(circle at 50% 52%, transparent 34%, rgba(0,0,0,0.28) 100%)'
            : 'radial-gradient(circle at 50% 50%, transparent 38%, rgba(0,0,0,0.22) 100%)',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          padding: 'clamp(14px, 10%, 22px) clamp(16px, 9%, 28px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          direction: rtl ? 'rtl' : 'ltr',
        }}
      >
        <div
          dir="ltr"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignSelf: 'stretch',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: isPhysical ? '4px' : 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 'calc(28px / 3)',
                flexShrink: 0,
              }}
            >
              <SimoAppMark size={28} outlineWhite />
              <span
                style={{
                  color: '#FFFFFF',
                  fontSize: '17.5px',
                  fontWeight: '700',
                  letterSpacing: '-0.038em',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                {'Simo Pay'}
              </span>
            </div>
            {isPhysical ? <PhysicalCardEmvChip /> : null}
          </div>
        </div>

        <div style={{ flex: 1, minHeight: '12px' }} />

        <div
          dir="ltr"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px' }}
        >
          <VisaWordmark />
          <div
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '15px',
              fontWeight: '550',
              color: '#FFFFFF',
              letterSpacing: '0.06em',
              fontVariantNumeric: 'tabular-nums',
              minWidth: 0,
              textAlign: 'right',
            }}
          >
            {isIssued && panLast4 ? <span>{panLast4}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

/** iOS-style two-tab slide; `dir="ltr"` keeps thumb geometry stable in RTL locales */
const CardKindSlide = ({ kind, setKind, virtualLabel, physicalLabel }) => {
  const isPhysical = kind === 'physical';
  const inset = 4;
  const midGap = 4;
  return (
    <div
      role="tablist"
      aria-label={virtualLabel + ' / ' + physicalLabel}
      dir="ltr"
      style={{
        position: 'relative',
        flexShrink: 0,
        marginTop: '12px',
        padding: inset,
        borderRadius: '999px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: inset,
          bottom: inset,
          left: inset,
          right: `calc(50% + ${midGap / 2}px)`,
          borderRadius: '999px',
          background: 'linear-gradient(135deg, #E8CA6A 0%, #D4AF37 45%, #B8962E)',
          boxShadow: '0 3px 16px rgba(212,175,55,0.32)',
          transition: 'transform 0.42s cubic-bezier(0.25, 1, 0.32, 1)',
          transform: isPhysical ? `translateX(calc(100% + ${midGap}px))` : 'translateX(0)',
          willChange: 'transform',
        }}
      />
      <div style={{ display: 'flex', gap: `${midGap}px`, position: 'relative', zIndex: 1 }}>
        <button
          type="button"
          role="tab"
          aria-selected={!isPhysical}
          onClick={() => setKind('virtual')}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            padding: '11px 8px',
            borderRadius: '999px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '800',
            letterSpacing: '-0.2px',
            fontFamily: 'Inter, sans-serif',
            color: !isPhysical ? '#0F0F0F' : '#8B93A7',
            background: 'transparent',
          }}
        >
          {virtualLabel}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={isPhysical}
          onClick={() => setKind('physical')}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            padding: '11px 8px',
            borderRadius: '999px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '800',
            letterSpacing: '-0.2px',
            fontFamily: 'Inter, sans-serif',
            color: isPhysical ? '#0F0F0F' : '#8B93A7',
            background: 'transparent',
          }}
        >
          {physicalLabel}
        </button>
      </div>
    </div>
  );
};

/** Fit-height apply flow — no scrolling */
const ChooseCardFlow = ({ t, isRTL, hasExistingCard, onBack, onApplied, kind, setKind }) => {
  const isPhysical = kind === 'physical';
  const fee = isPhysical ? t.applyFeePhysical : t.applyFeeVirtual;

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 16px 14px',
        direction: isRTL ? 'rtl' : 'ltr',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, minHeight: '36px' }}>
        {hasExistingCard ? (
          <motion.button
            type="button"
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            aria-label={t.back}
            style={{
              border: 'none',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '11px',
              color: '#F5F1EA',
              width: '36px',
              height: '36px',
              cursor: 'pointer',
              fontSize: '17px',
              lineHeight: 1,
              padding: 0,
            }}
          >
            ←
          </motion.button>
        ) : (
          <div style={{ width: '36px' }} />
        )}
      </div>

      <div style={{ flexShrink: 0 }}>
        <h2
          style={{
            color: '#F5F1EA',
            fontSize: '19px',
            fontWeight: '800',
            margin: 0,
            letterSpacing: '-0.55px',
            lineHeight: 1.2,
          }}
        >
          {t.cardsGetStarted || t.chooseCardTitle}
        </h2>
      </div>

      <CardKindSlide kind={kind} setKind={setKind} virtualLabel={t.virtualCardTab} physicalLabel={t.physicalCardTab} />

      {/*
        Top-align card slot (don't justifyContent:center) so switching virtual/physical
        doesn't re-center the card when blurb lines differ.
      */}
      <div style={{ flexShrink: 0, marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <SimoCardFace variant="preview" isPhysical={isPhysical} rtl={!!isRTL} />
        <p
          style={{
            textAlign: 'center',
            margin: 0,
            color: '#64708C',
            fontSize: '11px',
            lineHeight: 1.42,
            minHeight: '2.84em',
            maxHeight: '2.84em',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {isPhysical ? t.physicalCardBlurb : t.virtualCardBlurb}
        </p>
      </div>

      <div style={{ flex: 1, minHeight: 0 }} />

      <div style={{ flexShrink: 0 }}>
        <button
          type="button"
          data-testid="apply-simo-card"
          onClick={() => onApplied(isPhysical ? 'physical' : 'virtual')}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '14px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '800',
            letterSpacing: '-0.15px',
            color: '#0F0F0F',
            background: 'linear-gradient(135deg, #E8CA6A, #D4AF37 45%, #B8962E)',
            fontFamily: 'Inter, sans-serif',
            boxShadow: '0 8px 28px rgba(212,175,55,0.28)',
          }}
        >
          {t.applyForCard} · {fee}
        </button>
      </div>
    </div>
  );
};

const ActionRail = ({ label, Icon, onClick, emphasize }) => (
  <motion.button
    type="button"
    whileTap={{ scale: 0.96 }}
    onClick={onClick}
    style={{
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 4px',
      borderRadius: '12px',
      border: emphasize ? '1px solid rgba(212,175,55,0.35)' : '1px solid rgba(255,255,255,0.06)',
      background: emphasize ? 'rgba(212,175,55,0.07)' : 'rgba(255,255,255,0.025)',
      cursor: 'pointer',
    }}
  >
    <span style={{ display: 'grid', placeItems: 'center', height: '20px', width: '100%' }}>
      <Icon active={emphasize} />
    </span>
    <span style={{ color: emphasize ? '#D4AF37' : '#8B93A7', fontSize: '10px', fontWeight: '700', textAlign: 'center', lineHeight: 1.2 }}>
      {label}
    </span>
  </motion.button>
);

const IcoLayers = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#D4AF37' : '#9CA3AF'} strokeWidth="2">
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);
const IcoLock = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#FBBF24' : '#9CA3AF'} strokeWidth="2">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);
const IcoGauge = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#D4AF37' : '#9CA3AF'} strokeWidth="2">
    <path d="M12 20a8 8 0 008-8h-8V4a8 8 0 00-8 8 8 8 0 008 8z" />
    <path d="M12 14v4" />
  </svg>
);
const IcoDots = ({ active }) => {
  const c = active ? '#D4AF37' : '#9CA3AF';
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden fill="none">
      <circle cx="6" cy="12" r="1.5" fill={c} />
      <circle cx="12" cy="12" r="1.5" fill={c} />
      <circle cx="18" cy="12" r="1.5" fill={c} />
    </svg>
  );
};

/** Fits without scroll — tight vertical rhythm */
const CardsOverview = ({ t, isRTL, card, onAddCard, onOpenActivity }) => {
  const [frozen, setFrozen] = useState(false);
  const last4 = card?.last4 || '8109';

  const mockRows = [
    {
      id: 'r1',
      glyph: '🥐',
      name: 'Boulangerie Amir',
      sub: `${t.cardEndingIn} ${last4}`,
      amt: '-42.90 MAD',
      bad: false,
    },
    {
      id: 'r2',
      glyph: '🚖',
      name: 'Grand Taxi Desk',
      sub: `${t.cardEndingIn} ${last4}`,
      amt: '-80.00 MAD',
      bad: true,
      badLabel: t.txDeclined,
    },
  ];

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '8px 16px 6px',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div>
          <h2 style={{ color: '#F5F1EA', margin: 0, fontSize: '20px', fontWeight: '800', letterSpacing: '-0.5px', lineHeight: 1.1 }}>{t.cardsTitle}</h2>
        </div>
        <motion.button
          type="button"
          data-testid="cards-add"
          whileTap={{ scale: 0.94 }}
          onClick={onAddCard}
          style={{
            width: '38px',
            height: '38px',
            borderRadius: '12px',
            border: '1px solid rgba(212,175,55,0.35)',
            background: 'rgba(212,175,55,0.08)',
            color: '#E8CA6A',
            fontSize: '22px',
            lineHeight: 1,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingBottom: '2px',
          }}
          aria-label={t.addAnotherCard}
        >
          +
        </motion.button>
      </header>

      <div
        style={{
          marginTop: '24px',
          flexShrink: 0,
              position: 'relative',
          borderRadius: '22px',
          overflow: 'hidden',
          boxShadow: frozen ? '0 0 0 1px rgba(251,191,36,0.45), 0 18px 40px rgba(0,0,0,0.5)' : undefined,
        }}
      >
        <SimoCardFace variant="issued" isPhysical={card?.type === 'physical'} rtl={!!isRTL} panLast4={last4} />
        {frozen ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '22px',
              background: 'repeating-linear-gradient(135deg,rgba(19,19,22,0.92) 0,rgba(19,19,22,0.92) 6px,rgba(16,16,18,0.92) 6px,rgba(16,16,18,0.92) 12px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '800',
              color: '#FBBF24',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              pointerEvents: 'none',
            }}
          >
            {t.cardsActionFreeze}
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexShrink: 0 }}>
        <ActionRail label={t.cardsActionView} Icon={IcoLayers} onClick={() => {}} emphasize={false} />
        <ActionRail label={t.cardsActionFreeze} Icon={IcoLock} onClick={() => setFrozen((f) => !f)} emphasize={frozen} />
        <ActionRail label={t.cardsActionLimit} Icon={IcoGauge} onClick={() => {}} emphasize={false} />
        <ActionRail label={t.cardsActionSettings} Icon={IcoDots} onClick={() => {}} emphasize={false} />
      </div>

      <div style={{ marginTop: '10px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            padding: '6px 12px',
            borderRadius: '999px',
            border: '1px solid rgba(212,175,55,0.22)',
            background: 'rgba(212,175,55,0.06)',
            color: '#CDB87A',
            fontSize: '10.5px',
            fontWeight: '700',
            maxWidth: '100%',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          ✓ {t.cardsWalletReady}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexShrink: 0, marginBottom: '6px' }}>
          <span style={{ color: '#9DA6B9', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t.cardsTxTitle}</span>
          <motion.button
            type="button"
            whileTap={{ scale: 0.98 }}
            onClick={onOpenActivity}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '2px 0',
              color: '#D4AF37',
              fontSize: '11px',
              fontWeight: '700',
            }}
          >
            {t.cardsSeeActivity} →
          </motion.button>
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(255,255,255,0.02)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {mockRows.map((row) => (
            <div
              key={row.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '9px 12px',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
              }}
            >
              <span style={{ width: '34px', textAlign: 'center', flexShrink: 0, fontSize: '17px', lineHeight: 1 }}>{row.glyph}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#EDEEF4', fontSize: '12.5px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row.name}
                </div>
                <div style={{ color: '#697087', fontSize: '10.5px', marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row.sub}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ color: '#F5F1EA', fontSize: '12px', fontWeight: '700' }}>{row.amt}</div>
                {row.bad ? <div style={{ color: '#F87171', fontSize: '9.5px', fontWeight: '800', marginTop: '1px' }}>{row.badLabel}</div> : null}
          </div>
          </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/** Cards tab — fills phone viewport; overflow hidden throughout */
const WalletScreen = ({ onToast, onNavigate, t, isRTL }) => {
  const { simoCard, applySimoCard } = useWallet();
  const [kind, setKind] = useState('virtual');
  const [applyFlow, setApplyFlow] = useState(!simoCard);

  const tr = t || {};

  const handleApplied = (k) => {
    applySimoCard(k);
    setApplyFlow(false);
    if (onToast && tr.cardApplicationSent) {
      onToast(tr.cardApplicationSent);
    }
  };

  const showChooser = !simoCard || applyFlow;

  return (
    <div
      data-testid="wallet-screen"
      data-cards-mode={showChooser ? 'choose' : 'overview'}
      style={{
        background: '#0F0F0F',
        height: '100%',
        minHeight: 0,
        fontFamily: 'Inter, sans-serif',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      {showChooser ? (
        <ChooseCardFlow
          t={tr}
          isRTL={isRTL}
          hasExistingCard={!!simoCard}
          onBack={() => setApplyFlow(false)}
          onApplied={handleApplied}
          kind={kind}
          setKind={setKind}
        />
      ) : (
        <CardsOverview
          t={tr}
          isRTL={isRTL}
          card={simoCard}
          onAddCard={() => setApplyFlow(true)}
          onOpenActivity={() => {
            if (onNavigate) onNavigate('activity');
          }}
        />
      )}
    </div>
  );
};

export default WalletScreen;
